import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, GraduationCap, Film, ShieldCheck, Phone, MessageCircle } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from '../ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

export const Navbar = ({ onDemoClick }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    {
      name: 'Soluções',
      path: '/solucoes',
      children: [
        { name: 'Plataforma EAD', path: '/solucoes/plataforma-ead', icon: GraduationCap },
        { name: 'Produção de Cursos', path: '/solucoes/producao-cursos', icon: Film },
        { name: 'NR-01 Psicossocial', path: '/solucoes/nr01', icon: ShieldCheck },
      ],
    },
    { name: 'Cases', path: '/cases' },
    { name: 'Sobre', path: '/sobre' },
    { name: 'Blog', path: '/blog' },
    { name: 'Contato', path: '/contato' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/90 backdrop-blur-lg shadow-sm border-b border-slate-100'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img
              src="https://customer-assets.emergentagent.com/job_a8441df1-5f2f-4661-9386-182f70a7a0c4/artifacts/6sz36v20_Logo%20csp%20preto.png"
              alt="CSP Cursos Online"
              className="h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {navLinks.map((link) =>
              link.children ? (
                <DropdownMenu key={link.name}>
                  <DropdownMenuTrigger asChild>
                    <button
                      data-testid={`nav-${link.name.toLowerCase()}-dropdown`}
                      className={`flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                        location.pathname.startsWith('/solucoes')
                          ? 'text-blue-600 bg-blue-50'
                          : 'text-slate-600 hover:text-blue-600 hover:bg-blue-50'
                      }`}
                    >
                      {link.name}
                      <ChevronDown className="h-4 w-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-64 p-2">
                    {link.children.map((child) => (
                      <DropdownMenuItem key={child.path} asChild>
                        <Link
                          to={child.path}
                          data-testid={`nav-${child.name.toLowerCase().replace(/\s+/g, '-')}`}
                          className="flex items-center gap-3 px-3 py-3 rounded-lg cursor-pointer"
                        >
                          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-blue-50">
                            <child.icon className="h-5 w-5 text-blue-600" />
                          </div>
                          <span className="font-medium">{child.name}</span>
                        </Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link
                  key={link.name}
                  to={link.path}
                  data-testid={`nav-${link.name.toLowerCase()}`}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    isActive(link.path)
                      ? 'text-blue-600 bg-blue-50'
                      : 'text-slate-600 hover:text-blue-600 hover:bg-blue-50'
                  }`}
                >
                  {link.name}
                </Link>
              )
            )}
          </nav>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-3">
            <a
              href="https://wa.me/5500000000000"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="whatsapp-header-btn"
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
            >
              <MessageCircle className="h-4 w-4" />
              WhatsApp
            </a>
            <Button
              onClick={onDemoClick}
              data-testid="demo-btn-header"
              className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-full font-semibold shadow-lg shadow-orange-500/20 transition-all hover:-translate-y-0.5"
            >
              Solicitar Demonstração
            </Button>
          </div>

          {/* Mobile Menu */}
          <Sheet open={isMobileOpen} onOpenChange={setIsMobileOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <button
                data-testid="mobile-menu-trigger"
                className="p-2 text-slate-600 hover:text-blue-600"
              >
                <Menu className="h-6 w-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80 p-0">
              <div className="flex flex-col h-full">
                <div className="p-6 border-b">
                  <img
                    src="https://customer-assets.emergentagent.com/job_a8441df1-5f2f-4661-9386-182f70a7a0c4/artifacts/6sz36v20_Logo%20csp%20preto.png"
                    alt="CSP Cursos Online"
                    className="h-10 w-auto"
                  />
                </div>
                <nav className="flex-1 p-6">
                  <div className="space-y-2">
                    {navLinks.map((link) =>
                      link.children ? (
                        <div key={link.name} className="space-y-1">
                          <span className="block px-3 py-2 text-sm font-semibold text-slate-400 uppercase tracking-wider">
                            {link.name}
                          </span>
                          {link.children.map((child) => (
                            <Link
                              key={child.path}
                              to={child.path}
                              onClick={() => setIsMobileOpen(false)}
                              data-testid={`mobile-nav-${child.name.toLowerCase().replace(/\s+/g, '-')}`}
                              className="flex items-center gap-3 px-3 py-3 rounded-lg text-slate-600 hover:bg-blue-50 hover:text-blue-600"
                            >
                              <child.icon className="h-5 w-5" />
                              {child.name}
                            </Link>
                          ))}
                        </div>
                      ) : (
                        <Link
                          key={link.name}
                          to={link.path}
                          onClick={() => setIsMobileOpen(false)}
                          data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                          className={`block px-3 py-3 rounded-lg font-medium ${
                            isActive(link.path)
                              ? 'text-blue-600 bg-blue-50'
                              : 'text-slate-600 hover:bg-blue-50 hover:text-blue-600'
                          }`}
                        >
                          {link.name}
                        </Link>
                      )
                    )}
                  </div>
                </nav>
                <div className="p-6 border-t space-y-3">
                  <a
                    href="https://wa.me/5500000000000"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="mobile-whatsapp-btn"
                    className="flex items-center justify-center gap-2 w-full px-4 py-3 text-green-600 border border-green-200 rounded-lg font-medium hover:bg-green-50"
                  >
                    <MessageCircle className="h-5 w-5" />
                    WhatsApp
                  </a>
                  <Button
                    onClick={() => {
                      setIsMobileOpen(false);
                      onDemoClick();
                    }}
                    data-testid="mobile-demo-btn"
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3 rounded-lg font-semibold"
                  >
                    Solicitar Demonstração
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
